import { api, ApiError, changeNow } from './api'
import { bottomSheet } from './due-bar'
import { areaColours, areaOfCustomer } from './month-view'
import type { Customer } from './types'
import { getMe } from './me'
import { el, toast } from './ui'
import { offerUndo } from './undo'

// Rename an area (Liam, 27 Oct 2026, design A of https://claude.ai/artifact/1zfWhmDP4dh9gYqwKyavpb: "Would be good
// where ever these ie n11 i could tap and edit"). Tap N11 and say what it is called: the round's other areas as picture
// tiles, or a new name. Every customer shown under N11 across the round moves at once (functions/api/customers/
// area-rename.ts), with Undo; addresses and postcodes never change. Online only: the whole area or none of it.
// Opened from the Month's Areas pills, the Diary's and Customers' Area filters, a customer's Area, Messages'
// "They're in N11" and the area on a computer Diary job line.

// Who is in an area and every screen's filter follow a rename: screens listen for this (from, to).
export const AREA_RENAMED = 'roundly:area-renamed'
const OTHERS_SHOWN = 3

interface Saved { count: number; rows: Array<{ id: number; version: number; area_before: string | null }> }

export interface AreaRenameOptions {
  // "Show only N11": what the pill did before (a filter), kept in the pop-up when opened from a filter.
  showOnly?: () => void
}

export async function openAreaRename(label: string, opts: AreaRenameOptions = {}): Promise<void> {
  // Owner only, as the save is (workers reach only Today and My days anyway).
  if (!label || label === 'Not set' || getMe()?.role !== 'owner') return
  let all: Customer[]
  try { all = (await api<{ customers: Customer[] }>('/api/customers', 'customers')).customers }
  catch { toast('Could not load your customers. Check your signal and try again.'); return }
  // Customers whose details were binned are never renamed (the server refuses them too).
  const live = all.filter((c) => !(c as Customer & { details_binned_at?: string | null }).details_binned_at)
  const members = live.filter((c) => areaOfCustomer(c) === label)
  if (!members.length) { toast(`Nobody is in ${label} now.`); return }
  const counts = new Map<string, number>()
  for (const c of live) {
    const a = areaOfCustomer(c)
    if (a !== label && a !== 'Not set') counts.set(a, (counts.get(a) ?? 0) + 1)
  }
  const others = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  const colours = areaColours(live)
  const dot = (a: string): HTMLElement => el('i', { class: `area-dot ${colours.has(a) ? `area-${colours.get(a)! + 1}` : 'area-none'}`, 'aria-hidden': 'true' })
  const people = (n: number): string => `${n} customer${n === 1 ? '' : 's'}`

  const sheet = bottomSheet(`Rename ${label}`, () => {})
  sheet.dialog.classList.add('area-sheet')
  const first = (c: Customer): string => c.name.trim().split(/\s+/)[0] ?? c.name
  const names = members.slice(0, 3).map(first)
  const who = members.length <= 4 ? members.map(first) : [...names, `${members.length - 3} more`]
  const whoWords = who.length > 1 ? `${who.slice(0, -1).join(', ')} and ${who[who.length - 1]}` : who[0]
  sheet.head.append(el('h3', { class: 'area-sheet-title' }, dot(label), label), el('span', { class: 'area-sheet-line', 'aria-hidden': 'true' }),
    el('p', { class: 'area-sheet-who muted' }, `${people(members.length)} on your round: ${whoWords}.`))

  let chosen: string | null = null
  let typing = false
  const tiles = el('div', { class: 'area-tiles', role: 'group', 'aria-label': 'What do you call this area?' })
  const input = el('input', { type: 'text', class: 'area-new-input', maxlength: '200', autocomplete: 'off', 'aria-label': "The new area's name" }) as HTMLInputElement
  const typed = el('label', { class: 'area-new', hidden: '' }, el('span', {}, "The new area's name"), input)
  const go = el('button', { type: 'button', class: 'btn-primary area-go', disabled: '' }, 'Pick a name') as HTMLButtonElement
  const problem = el('p', { class: 'area-problem', role: 'alert', hidden: '' })
  // The name it will save: a typed name that is an existing area apart from capitals or spaces joins that area.
  const target = (): string => {
    if (!typing) return chosen ?? ''
    const t = input.value.trim().replace(/\s+/g, ' ')
    return others.find(([a]) => a.toLowerCase() === t.toLowerCase())?.[0] ?? t
  }
  const refresh = (): void => {
    const t = target()
    const ok = !!t && t !== label && t.toLowerCase() !== 'not set'
    go.disabled = !ok
    go.textContent = ok ? `Call them ${t}` : t === label ? 'That is already its name' : t.toLowerCase() === 'not set' ? 'Choose a name for the area' : 'Pick a name'
    for (const b of tiles.querySelectorAll<HTMLButtonElement>('.area-tile')) {
      const on = b.dataset.new === '1' ? typing : !typing && b.dataset.area === chosen
      b.classList.toggle('on', on); b.setAttribute('aria-pressed', String(on))
    }
    typed.hidden = !typing
  }
  const tile = (a: string, n: number): HTMLElement => {
    const b = el('button', { type: 'button', class: 'area-tile', 'data-area': a, 'aria-pressed': 'false' },
      el('span', { class: 'area-tile-ring' }, dot(a)), el('span', { class: 'area-tile-words' }, el('strong', {}, a), el('span', {}, people(n))))
    b.addEventListener('click', () => { chosen = a; typing = false; problem.hidden = true; refresh() })
    return b
  }
  for (const [a, n] of others.slice(0, OTHERS_SHOWN)) tiles.append(tile(a, n))
  const fresh = el('button', { type: 'button', class: 'area-tile', 'data-new': '1', 'aria-pressed': 'false' },
    el('span', { class: 'area-tile-ring area-tile-plus', 'aria-hidden': 'true' }, '+'), el('span', { class: 'area-tile-words' }, el('strong', {}, 'New name'), el('span', {}, 'Type your own')))
  fresh.addEventListener('click', () => { typing = true; problem.hidden = true; refresh(); input.focus() })
  tiles.append(fresh)
  input.addEventListener('input', refresh)
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !go.disabled) { e.preventDefault(); go.click() } })
  sheet.body.append(el('h4', { class: 'area-question' }, 'What do you call this area?'), tiles)
  if (others.length > OTHERS_SHOWN) {
    const more = el('button', { type: 'button', class: 'link-button area-more' }, `More areas (${others.length - OTHERS_SHOWN})`)
    more.addEventListener('click', () => { for (const [a, n] of others.slice(OTHERS_SHOWN)) fresh.before(tile(a, n)); more.remove(); refresh() })
    sheet.body.append(more)
  }
  sheet.body.append(typed, problem,
    el('p', { class: 'area-note muted' }, 'Only the area changes. Their addresses and postcodes stay as they are, and Undo puts them all back.'), go)
  if (opts.showOnly) {
    const only = el('button', { type: 'button', class: 'btn-secondary area-only' }, `Show only ${label}`)
    only.addEventListener('click', () => { sheet.close(); opts.showOnly!() })
    sheet.body.append(only)
  }

  // The same key for a second try after no answer, so it is never saved twice (the server answers a repeat with the
  // first answer).
  let key = crypto.randomUUID()
  let sentFor = ''
  go.addEventListener('click', async () => {
    const to = target()
    if (!to || to === label || to.toLowerCase() === 'not set') return
    if (sentFor !== to) { key = crypto.randomUUID(); sentFor = to }
    go.disabled = true; go.textContent = 'Saving…'; problem.hidden = true
    try {
      const saved = await changeNow<Saved>('POST', '/api/customers/area-rename', { from: label, to, rows: members.map((c) => ({ id: c.id, version: c.version })) }, key)
      sheet.close()
      // Joined an area that was already there: its own customers stay in it, so Undo leaves a filter on it alone.
      const merged = others.some(([a]) => a === to)
      renamed(label, to)
      const undoKey = crypto.randomUUID()
      offerUndo(`${people(saved.count)} moved from ${label} to ${to}`, () => void undo(saved, label, to, merged, undoKey))
    } catch (e) {
      const err = e as ApiError
      problem.hidden = false
      problem.textContent = err.status === 0
        ? 'No answer: check your signal, then tap again. It will not be saved twice.'
        : err.status === 409 ? `${err.message} Close this and tap ${label} again.` : err.message || 'Could not rename it.'
      go.disabled = false; refresh()
      if (err.status === 0) go.textContent = 'Try again'
    }
  })
  refresh()
  sheet.dialog.showModal()
}

// key: one for this Undo, kept across its tries, so a second try after a lost answer gets the first answer.
async function undo(saved: Saved, from: string, to: string, merged: boolean, key: string): Promise<void> {
  try {
    await changeNow('POST', '/api/customers/area-rename', { restore: true, rows: saved.rows.map((r) => ({ id: r.id, version: r.version, area: r.area_before })) }, key)
    if (merged) window.dispatchEvent(new CustomEvent('dropby:data-changed'))
    else renamed(to, from)
    toast(`Put back in ${from}.`)
  } catch (e) {
    // No answer: Undo is offered again, with the same key, so it can never be done twice.
    if ((e as ApiError).status === 0) offerUndo(`No signal: ${from} is not back yet`, () => void undo(saved, from, to, merged, key))
    else toast((e as ApiError).message || 'Undo did not work.')
  }
}

// Every screen reads again, and any filter on the old name follows it.
function renamed(from: string, to: string): void {
  window.dispatchEvent(new CustomEvent(AREA_RENAMED, { detail: { from, to } }))
  window.dispatchEvent(new CustomEvent('dropby:data-changed'))
}

// "Rename this area" beside a filter that has an area chosen (the Diary's Find a visit, Customers and Work).
export function areaRenameLink(label: string, words = 'Rename this area'): HTMLElement {
  const b = el('button', { type: 'button', class: 'link-button area-rename-link' }, words)
  b.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); void openAreaRename(label) })
  return b
}

// An area named in a sentence ("They're in N11" on a Messages card): the name itself opens the pop-up.
export function areaWord(label: string): HTMLElement | string {
  if (!label || label === 'Not set') return label
  const b = el('button', { type: 'button', class: 'link-button area-word', title: `Rename ${label}` }, label)
  b.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); void openAreaRename(label) })
  return b
}
